import { useEffect, useRef, useSyncExternalStore } from "react";
import { Modal } from "./components/Modal";
import { rememberDialogTrigger } from "./dialog";

type Confirmation = { title: string; description: string; confirmLabel?: string; danger?: boolean };
type Pending = Confirmation & { resolve: (value: boolean) => void };
const queue: Pending[] = [];
const listeners = new Set<() => void>();
const dirtyForms = new Map<symbol, () => { dirty: boolean; label: string; pending: boolean }>();
function emit() { listeners.forEach((listener) => listener()); }
export function confirmAction(options: Confirmation): Promise<boolean> {
  return new Promise((resolve) => { queue.push({ ...options, resolve }); emit(); });
}
export function confirmDiscard(dirty: boolean, label: string): Promise<boolean> {
  return dirty ? confirmAction({ title: "有尚未保存的修改", description: `${label}尚未保存。离开会放弃当前修改，已经保存的内容不受影响。`, confirmLabel: "放弃修改并继续", danger: true }) : Promise.resolve(true);
}
export async function confirmNavigation(): Promise<boolean> {
  const forms = [...dirtyForms.values()].map((read) => read());
  const submitting = forms.filter((item) => item.pending);
  if (submitting.length) {
    await confirmAction({ title: "当前操作正在提交", description: `${[...new Set(submitting.map((item) => item.label))].join("、")}正在提交。为防止丢失操作结果，当前页面暂不切换；已经在服务端运行的生成任务不受影响。`, confirmLabel: "继续留在当前页面" });
    return false;
  }
  const changed = forms.filter((item) => item.dirty);
  return confirmDiscard(changed.length > 0, [...new Set(changed.map((item) => item.label))].join("、"));
}
/** Register values, not snapshots, so a successful save immediately clears the guard. */
export function useUnsavedChanges(dirty: boolean, label: string, pending = false) {
  const latest = useRef({ dirty, label, pending }); latest.current = { dirty, label, pending };
  useEffect(() => { const id = Symbol(label); dirtyForms.set(id, () => latest.current); return () => { dirtyForms.delete(id); }; }, []);
}
export function InteractionHost() {
  const pending = useSyncExternalStore((listener) => { listeners.add(listener); return () => listeners.delete(listener); }, () => queue[0] ?? null);
  useEffect(() => {
    const beforeUnload = (event: BeforeUnloadEvent) => {
      if ([...dirtyForms.values()].some((read) => { const value = read(); return value.dirty || value.pending; })) { event.preventDefault(); event.returnValue = ""; }
    };
    window.addEventListener("beforeunload", beforeUnload);
    document.addEventListener("pointerdown", rememberDialogTrigger, true);
    document.addEventListener("keydown", rememberDialogTrigger, true);
    return () => {
      window.removeEventListener("beforeunload", beforeUnload);
      document.removeEventListener("pointerdown", rememberDialogTrigger, true);
      document.removeEventListener("keydown", rememberDialogTrigger, true);
    };
  }, []);
  function finish(value: boolean) { const item = queue.shift(); item?.resolve(value); emit(); }
  return <Modal open={Boolean(pending)} title={pending?.title ?? "确认操作"} width="small" onClose={() => finish(false)} footer={<><button data-autofocus type="button" className="button button-secondary" onClick={() => finish(false)}>取消，留在当前页面</button><button type="button" className={`button ${pending?.danger ? "button-danger" : "button-primary"}`} onClick={() => finish(true)}>{pending?.confirmLabel ?? "确认继续"}</button></>}><p className="ux-confirm-copy">{pending?.description}</p></Modal>;
}

/** Preferences must not prevent reading/writing when storage is unavailable. Never store secrets here. */
export const safeStorage = {
  getItem(key: string): string | null { try { return window.localStorage.getItem(key); } catch { return null; } },
  setItem(key: string, value: string): void { try { window.localStorage.setItem(key, value); } catch { /* Preferences are optional; manuscript saving uses the API. */ } }
};

export function useSaveShortcut(save: () => void | Promise<void>, enabled: boolean) {
  const latest = useRef({ save, enabled }); latest.current = { save, enabled };
  useEffect(() => {
    const key = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "s" && !event.isComposing && !document.querySelector('[role="dialog"]')) {
        event.preventDefault(); if (latest.current.enabled) void latest.current.save();
      }
    };
    window.addEventListener("keydown", key); return () => window.removeEventListener("keydown", key);
  }, []);
}
